// src/components/workspace/index.tsx
import { useState } from 'react';
import { Box } from '@mui/material';
import { useAssignees } from './hooks/useAssignees';
import { useStatuses } from './hooks/useStatuses';
import { useWorkstreams } from './hooks/useWorkstreams';
import { WorkspaceHeader } from './WorkspaceHeader';
import { WorkspaceContent } from './WorkspaceContent';
import { Sidebar } from './Sidebar';
import { AssigneeManager } from './AssigneeManager';
import { StatusManager } from './StatusManager';
import { WorkstreamTypeManager } from './WorkstreamTypeManager';
import { useWorkstreamTypes } from './hooks/useWorkstreamTypes';
import { WorkspaceProvider, useWorkspaceContext } from './context/WorkspaceContext';

interface WorkspaceProps {
  workspaceId: string;
  onExit: () => void;
}

export const Workspace = ({ workspaceId, onExit }: WorkspaceProps) => {
  return (
    <WorkspaceProvider workspaceId={workspaceId}>
      <WorkspaceLayout onExit={onExit} />
    </WorkspaceProvider>
  );
};

const WorkspaceLayout = ({ onExit }: { onExit: () => void }) => {
  const { workspaceId } = useWorkspaceContext();
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [showAssigneeManager, setShowAssigneeManager] = useState(false);
  const [showStatusManager, setShowStatusManager] = useState(false);
  const [showWorkstreamTypeManager, setShowWorkstreamTypeManager] = useState(false);
  const [showAddWorkstreamDialog, setShowAddWorkstreamDialog] = useState(false);

  const { 
    assignees,
    addAssignee,
    updateAssignee,
    deleteAssignee
  } = useAssignees(workspaceId);

  const {
    statuses,
    addStatus,
    updateStatus,
    deleteStatus
  } = useStatuses(workspaceId);

  const { 
    workstreamTypes,
    addWorkstreamType,
    updateWorkstreamType,
    deleteWorkstreamType
  } = useWorkstreamTypes(workspaceId);

  const {
    workstreams,
    status: workstreamsStatus,
    addWorkstream,
    updateWorkstream,
    deleteWorkstream,
    updateWorkstreamMilestones
  } = useWorkstreams();

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar
        isExpanded={sidebarExpanded}
        onToggle={() => setSidebarExpanded(!sidebarExpanded)}
        onManageTypes={() => setShowWorkstreamTypeManager(true)}
        onManageTeam={() => setShowAssigneeManager(true)}
        onManageStatuses={() => setShowStatusManager(true)}
        onExit={onExit}
      />

      <Box
        sx={{
          flexGrow: 1,
          ml: sidebarExpanded ? '240px' : '56px',
          transition: 'margin-left 0.2s ease',
          px: 3,
        }}
      >
        <WorkspaceHeader />
        <WorkspaceContent
          workstreams={workstreams}
          isLoading={workstreamsStatus === 'loading'}
          assignees={assignees}
          onAdd={addWorkstream}
          onUpdate={updateWorkstream}
          onDelete={deleteWorkstream}
          onUpdateMilestones={updateWorkstreamMilestones}
          showAddDialog={showAddWorkstreamDialog}
          onAddDialogClose={() => setShowAddWorkstreamDialog(!showAddWorkstreamDialog)}
        />
      </Box>

      <AssigneeManager
        open={showAssigneeManager}
        onClose={() => setShowAssigneeManager(false)}
        assignees={assignees}
        onAdd={addAssignee}
        onUpdate={updateAssignee}
        onDelete={deleteAssignee}
      />

      <StatusManager
        open={showStatusManager}
        onClose={() => setShowStatusManager(false)}
        statuses={statuses}
        onAdd={addStatus}
        onUpdate={updateStatus}
        onDelete={deleteStatus}
      />

      <WorkstreamTypeManager
        open={showWorkstreamTypeManager}
        onClose={() => setShowWorkstreamTypeManager(false)}
        workstreamTypes={workstreamTypes}
        onAdd={addWorkstreamType}
        onUpdate={updateWorkstreamType}
        onDelete={deleteWorkstreamType}
      />
    </Box>
  );
};